<template>
    <label
        class="ds-checkbox"
        :class="[{ disabled: disabled, 'has-error': !isValid, checked: isChecked }, wrapperClass]"
        :data-test="dataTestLabel"
    >
        <input
            type="checkbox"
            class="ds-checkbox__input"
            :id
            :name
            :value
            :disabled
            :required
            v-model="modelValue"
            v-bind="attrs"
            :data-test-value="dataTestValueInput"
        />
        <div
            class="ds-checkbox__checkbox"
            tabindex="0"
            @keydown="checkboxKeydownEventHandler"
            role="checkbox"
            :aria-checked="isChecked"
            :aria-required="required"
            :aria-disabled="disabled"
            :aria-invalid="!isValid"
            :aria-labelledby="ariaLabelId"
        >
            <DsIcon
                :width="16"
                :height="16"
                name="check"
                class="ds-checkbox__checkmark"
                :is-in-storybook="isInStorybook"
            />
        </div>
        <div class="ds-checkbox__texts" :data-test="dataTest">
            <span class="ds-checkbox__label" v-html="label" :id="ariaLabelId"></span>
            <span class="ds-checkbox__message" v-if="supportingText">{{ supportingText }}</span>
        </div>
        <slot></slot>
    </label>
</template>

<script lang="ts" setup generic="T extends DsFormFieldValueType">
import { computed, ComputedRef, ref, useAttrs } from "vue";
import DsIcon from "@sazka/design-system/src/icon/ds-icon.vue";
import { DsFormFieldValueType } from "../models/ds-form-field-models";
import { DsCheckboxListOptionModel } from "./ds-checkbox-option/ds-checkbox-option-models";

const props = defineProps<
    DsCheckboxListOptionModel & {
        /**
         * Generic overrire for value option.
         */
        value: T;

        /**
         * Group name for the checkboxes.
         */
        name: string;

        /**
         * Indicates whether the component is rendered within Storybook. This prop is needed to DsIcon component for correct rendering.
         */
        isInStorybook?: boolean;
    }
>();

defineOptions({ inheritAttrs: false });
const { class: wrapperClass, ...attrs } = useAttrs();

/**
 * Model for the checkbox value. All checkboxes store their values in an array if is checked.
 */
const modelValue = defineModel<T[]>();
const errorMessages = ref<string[]>([]);

const isValid = computed(() => !errorMessages.value.length);
const isChecked = computed(() => modelValue.value.includes(props.value));

defineExpose<{ isChecked: ComputedRef<boolean> }>({
    isChecked,
});

const checkboxKeydownEventHandler = (event: KeyboardEvent) => {
    if (!props.disabled && (event.key === "Enter" || event.key === "Space")) {
        if (isChecked.value) {
            const valueIndex = modelValue.value.indexOf(props.value);
            modelValue.value.splice(valueIndex, 1);
        } else {
            modelValue.value.push(props.value);
        }
    }
};

const ariaLabelId = computed(() => `label-${props.id.replace(/\s+/g, "-").toLowerCase()}`);
</script>
